import { OrderType, ProductType } from '../interfaces/Order';

export enum OrderStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  RESHIPPED = 'reshipped',
  CANCELLED = 'cancelled',
}

export enum ShipmentStatus {
  PRE_TRANSIT = 'pre_transit',
  IN_TRANSIT = 'in_transit',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  AVAILABLE_FOR_PICKUP = 'available_for_pickup',
  RETURN_TO_SENDER = 'return_to_sender',
  FAILURE = 'failure',
  CANCELLED = 'cancelled',
  ERROR = 'error',
  UNKNOWN = 'unknown',
}

export enum ResendRequestFilter {
  PENDING_RESEND = 'pending_resend',
  RESEND_APPROVED = 'resend_approved',
  RESEND_REJECTED = 'resend_rejected',
}

export enum Country {
  CANADA = 'ca',
  US = 'us',
}

/** Seller-facing country shipping fee (not EasyPost shippingCost). */
export const CountryShippingFeeMap: Record<Country, number> = {
  [Country.US]: 0.99,
  [Country.CANADA]: 1.99,
};

export enum TrackingFilter {
  NoTracking = 'no_tracking',
  TrackingOnly = 'tracking_only',
  ShipmentOnly = 'shipment_only',
  NeedsShippoPurchase = 'needs_shippo_purchase',
}

/** Platform an admin-purchased shipping label was bought on. */
export enum LabelProvider {
  EasyPost = 'easypost',
  Shippo = 'shippo',
}

/** Admin-only order list filter by label platform / pending Shippo queue. */
export enum LabelFilter {
  ShippoPending = 'shippo_pending',
  ShippoBought = 'shippo_bought',
  EasypostBought = 'easypost_bought',
}

export const OrderItems = [
  {
    label: 'Sticker',
    value: ProductType.sticker,
  },
  {
    label: 'Phone Case',
    value: ProductType.iphone,
  },
];

export const OrderProductTypeMap: Record<OrderType, ProductType> = {
  phone_case: ProductType.iphone,
  sticker: ProductType.sticker,
  magnet_sticker: ProductType.magnet_sticker,
  poster: ProductType.poster,
  uvdtf: ProductType.uvdtf,
  badge_reel: ProductType.badge_reel,
  glass_suncatcher: ProductType.glass_suncatcher,
  squishy_belly: ProductType.squishy_belly,
  card_skin: ProductType.card_skin,
  ornament: ProductType.ornament,
};

export const OrderDetailTitleMap: Record<ProductType, string> = {
  sticker: 'Sticker',
  magnet_sticker: 'Magnet Sticker',
  iphone: 'Phone Case',
  poster: 'Poster',
  uvdtf: 'UVDTF',
  badge_reel: 'Badge Reel',
  glass_suncatcher: 'Glass Suncatcher',
  squishy_belly: 'Squishy Belly',
  card_skin: 'Card Skin',
  ornament: 'Ornament',
};

export const ProductTypeOptions = [
  { label: 'View All', value: '' },
  { label: 'Sticker', value: ProductType.sticker },
  { label: 'Magnet Sticker', value: ProductType.magnet_sticker },
  { label: 'Phone Case', value: ProductType.iphone },
  { label: 'Poster', value: ProductType.poster },
  { label: 'UVDTF', value: ProductType.uvdtf },
  { label: 'Badge Reel', value: ProductType.badge_reel },
  { label: 'Glass Suncatcher', value: ProductType.glass_suncatcher },
  { label: 'Squishy Belly', value: ProductType.squishy_belly },
  { label: 'Card Skin', value: ProductType.card_skin },
  { label: 'Ornament', value: ProductType.ornament },
];

export const StatusFilterOptions = [
  { label: 'All', value: '' },
  { label: 'Pending', value: OrderStatus.PENDING },
  { label: 'Processing', value: OrderStatus.PROCESSING },
  { label: 'Shipped', value: OrderStatus.SHIPPED },
  { label: 'Cancelled', value: OrderStatus.CANCELLED },
  {
    label: 'Pending Resend',
    value: ResendRequestFilter.PENDING_RESEND,
  },
  {
    label: 'Resend Approved',
    value: ResendRequestFilter.RESEND_APPROVED,
  },
  {
    label: 'Resend Rejected',
    value: ResendRequestFilter.RESEND_REJECTED,
  },
];

export const PRODUCT_TYPE_CHOICE_KEY = 'product_type_choice';

export const RequestAdminPurchaseShipment = 'CẦN TRACKING';

/** Sentinels that mean admin should buy a Shippo label. */
export const NeedsShippoPurchaseSentinels = [
  RequestAdminPurchaseShipment,
  'Cần mua tracking',
] as const;

export const isNeedsShippoPurchase = (
  trackingReference?: string | null,
  shippingLabelUrl?: string | null,
  shipmentId?: string | null,
): boolean => {
  if (!trackingReference) return false;
  if (shippingLabelUrl || shipmentId) return false;
  return (NeedsShippoPurchaseSentinels as readonly string[]).includes(
    trackingReference,
  );
};

/**
 * Product types eligible for admin Shippo label purchase.
 * Keep in sync with API ShippoEligibleOrderTypes (stickers-api
 * src/constants/shippo.ts) — this is the single web-side source of truth.
 */
export const ShippoEligibleOrderTypes: ReadonlyArray<OrderType> = [
  OrderType.Sticker,
  OrderType.MagnetSticker,
  OrderType.Ornament,
];

export const isShippoEligibleOrderType = (type?: string | null): boolean => {
  // Legacy orders without a type are stickers.
  if (!type) return true;
  return (ShippoEligibleOrderTypes as readonly string[]).includes(type);
};

export const isEligibleForShippoPurchase = (order?: {
  type?: string | null;
  country?: string | null;
  status?: string | null;
  trackingReference?: string | null;
  shippingLabelUrl?: string | null;
  shipmentId?: string | null;
} | null): boolean => {
  if (!order) return false;
  const eligibleType = isShippoEligibleOrderType(order.type);
  // Shippo scope is US-only (no international shipping in v1)
  const eligibleCountry = order.country === Country.US;
  // Cancelled/shipped orders are out of the buy queue.
  const eligibleStatus =
    !order.status ||
    order.status === OrderStatus.PENDING ||
    order.status === OrderStatus.PROCESSING;
  return (
    eligibleType &&
    eligibleCountry &&
    eligibleStatus &&
    isNeedsShippoPurchase(
      order.trackingReference,
      order.shippingLabelUrl,
      order.shipmentId,
    )
  );
};

export const ShipWithHardTubeExtraCharge = 1.5;
